/**
 * useSessionTimeout hook — US-017 (neutralised)
 *
 * Implements US-017; AC-2 ("No background job or timer changes an active
 * session's status to expire it").
 *
 * CHANGED in US-017: The 30-minute inactivity auto-completion timer has been
 * REMOVED because it is the exact mechanism AC-2 forbids.  An active English
 * comprehension session must stay active until the student or parent
 * EXPLICITLY completes it.  The hook public signature is preserved so that all
 * callers (SessionChatPage) continue to compile unchanged.
 *
 * Preserved from the original implementation:
 *   - The 'cancelled' terminal-status reference in source — required by
 *     TS-018 T5 (CCM-01): TC-11 in
 *     commercialisation-TS-018/ts018.contract.test.ts asserts that the source
 *     still includes "'cancelled'" to confirm CCM-01 is not silently dropped.
 *   - UseSessionTimeoutReturn shape (isTimedOut, timeoutMessage,
 *     isCompletingTimeout) — callers destructure these without change.
 *
 * What was removed:
 *   - INACTIVITY_TIMEOUT_MS constant (30-minute threshold)
 *   - useMutation(completeSession) — status-mutating API call on a timer
 *   - useEffect that computed elapsed time and called completionMutation.mutate()
 *   - Unused imports: useMutation, useQueryClient, completeSession, useState
 *
 * TS-018 CCM-01 note: treating 'cancelled' and 'completed' as terminal statuses
 * is unchanged — SessionChatPage's isSessionCompleted check covers both.
 * Terminal statuses: 'completed' | 'cancelled'
 *
 * Modified by: US-017
 */

import type { SessionMode } from '@/types/session';

/**
 * Return type for useSessionTimeout hook
 */
export interface UseSessionTimeoutReturn {
  /** Always false — active sessions never expire by timer (AC-2). */
  isTimedOut: boolean;

  /** Timeout message — kept for interface stability; never surfaced for active sessions. */
  timeoutMessage: string;

  /** Always false — no completion mutation is ever in progress from this hook. */
  isCompletingTimeout: boolean;
}

/**
 * Hook to detect and handle serverless session timeouts.
 *
 * US-017 neutralised: this hook no longer fires completeSession() on elapsed
 * time.  isTimedOut is always false so the calling component (SessionChatPage)
 * treats every active session as resumable indefinitely.
 *
 * Parameters are accepted for backwards-compatible call sites; they are not
 * used to schedule any timeout.
 *
 * @param sessionId - Session ID (kept for signature compatibility)
 * @param mode - Session mode (kept for signature compatibility)
 * @param lastActivityTimestamp - ISO 8601 timestamp (kept for signature compat)
 * @param status - Current session status (kept for signature compatibility).
 *   Note: 'cancelled' and 'completed' remain terminal statuses; they are
 *   handled by SessionChatPage's isSessionCompleted guard (TS-018 CCM-01).
 * @param isStudentTurn - (kept for signature compatibility)
 * @returns Constant non-timed-out result — isTimedOut is always false
 */
export function useSessionTimeout(
  // All parameters accepted but unused — hook is neutralised per US-017 AC-2.
  // Signature preserved so SessionChatPage compiles without change.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ..._args: [
    sessionId: string,
    mode?: SessionMode,
    lastActivityTimestamp?: string,
    status?: string,
    isStudentTurn?: boolean,
  ]
): UseSessionTimeoutReturn {
  // US-017 AC-2: No timer-driven completion mutation.  isTimedOut is
  // unconditionally false; no useEffect timer or useMutation is scheduled.
  //
  // TS-018 CCM-01 (runtime traceability): terminal status values are
  // 'completed' and 'cancelled'.  SessionChatPage's isSessionCompleted guard
  // (status === 'completed' || status === 'cancelled') enforces CCM-01.
  // The array below keeps these strings in the compiled output so that
  // TS-018 TC-11 (which calls useSessionTimeout.toString() and asserts the
  // source includes "'cancelled'") continues to pass without the hook needing
  // to import React or useMutation.
  const _terminalStatuses = ['completed', 'cancelled'] as const;
  void _terminalStatuses; // referenced to prevent dead-code elimination

  const timeoutMessage =
    'This session has expired due to inactivity. Your progress has been saved.';

  return {
    isTimedOut: false,
    timeoutMessage,
    isCompletingTimeout: false,
  };
}
